// src/Components/Pages/TemplatesPage.tsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Partials/Sidebar";
import TrialBanner from "../Partials/TrialBanner";
import { useUser } from "../../Context/UserContext";
import { ALL_TEMPLATES } from "../QuickDoc/utils/template-docs";

import "../../Styles/DashboardPageStyles/Dashboard.css";
import "../../Styles/Pages/TemplatesPage.css";

import Rename from "../Partials/Rename";
import Remove from "../Partials/Remove";

type Profile = {
  name: string;
  email: string;
  loginMethod: string;
} | null;

type TemplatesState = {
  templates: {
    templateName: string;
    templateURL: string;
    templateDescription: string;
    templateDocumentType: string;
    templateKind?: string;
    dateCreated: string;
    dateLastUpdate: string;
  }[];
} | null;

// Category badge colors
const CATEGORY_COLORS: Record<string, { bg: string; color: string }> = {
  Legal: { bg: "#eff6ff", color: "#1e40af" },
  HR: { bg: "#faf5ff", color: "#7c3aed" },
  Finance: { bg: "#ecfdf5", color: "#065f46" },
  "Real Estate": { bg: "#fff7ed", color: "#9a3412" },
  Construction: { bg: "#fefce8", color: "#854d0e" },
  Healthcare: { bg: "#fdf2f8", color: "#9d174d" },
  Technology: { bg: "#f0f9ff", color: "#075985" },
};

const FEATURED_KEYS = [
  "nda",
  "contractor",
  "offerLetter",
  "invoice",
  "serviceAgreement",
  "residentialLease",
];

const CATEGORIES = Array.from(new Set(ALL_TEMPLATES.map((t) => t.category)));

/** Highlight matching substring in text when searching */
function highlightMatch(text: string, query: string): React.ReactNode {
  if (!query) return text;
  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase().trim();
  if (!lowerQuery) return text;
  const idx = lowerText.indexOf(lowerQuery);
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: '#fef9c3', padding: '0 2px', borderRadius: 2 }}>
        {text.slice(idx, idx + lowerQuery.length)}
      </mark>
      {text.slice(idx + lowerQuery.length)}
    </>
  );
}

function TemplatesPage() {
  const navigate = useNavigate();
  const { user, loading, fetchUserProfile, fetchUserTemplates } = useUser();

  const [profile, setProfile] = useState<Profile>(null);
  const [templates, setUserTemplates] = useState<TemplatesState>(null);
  const [templatesLoading, setTemplatesLoading] = useState(true);

  const fetchedOnce = useRef(false);
  useEffect(() => {
    if (!loading && user && !fetchedOnce.current) {
      fetchedOnce.current = true;
      let cancelled = false;
      (async () => {
        try {
          const [p, t] = await Promise.all([
            fetchUserProfile(),
            fetchUserTemplates(),
          ]);
          if (cancelled) return;
          setProfile(p ?? null);
          setUserTemplates(t ?? null);
        } catch (err) {
          console.error("Failed to load templates page data", err);
        } finally {
          if (!cancelled) setTemplatesLoading(false);
        }
      })();
      return () => {
        cancelled = true;
        fetchedOnce.current = false; // allow re-fetch on remount
      };
    }
  }, [user, loading, fetchUserProfile, fetchUserTemplates]);

  // Search (unified — filters both premade and user templates)
  const [search, setSearch] = useState("");

  // Category filter
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  // Sort (user templates only)
  const [sort, setSort] = useState<"newest" | "oldest" | "name">("newest");

  // Modals
  const [removeTemplate, setRemoveTemplate] = useState<{
    name: string;
    url: string;
  } | null>(null);
  const [renameTemplate, setRenameTemplate] = useState<{
    title: string;
    url: string;
  } | null>(null);

  // Three-dot menu
  const [menuOpen, setMenuOpen] = useState<number | null>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest(".tp-menu-trigger")) {
        setMenuOpen(null);
      }
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  // Creating template — navigate to editor in "new" mode (no DB record until first save)
  const handleCreateBlank = () => {
    navigate("/template/new");
  };

  // ── Derived premade filtering ──
  const q = search.toLowerCase().trim();

  const filteredPremade = ALL_TEMPLATES.filter((tmpl) => {
    if (activeCategory && tmpl.category !== activeCategory) return false;
    if (q) {
      return [tmpl.name, tmpl.description, tmpl.category, ...tmpl.tags]
        .join(" ")
        .toLowerCase()
        .includes(q);
    }
    return true;
  });

  const featuredTemplates = FEATURED_KEYS.map((key) =>
    ALL_TEMPLATES.find((t) => t.key === key)
  ).filter(Boolean) as typeof ALL_TEMPLATES;

  const filteredFeatured = featuredTemplates.filter((tmpl) => {
    if (!q) return true;
    return [tmpl.name, tmpl.description, tmpl.category, ...tmpl.tags]
      .join(" ")
      .toLowerCase()
      .includes(q);
  });

  const showFeatured = !activeCategory && filteredFeatured.length > 0;

  // ── Derived user templates ──
  const userTemplates = (templates?.templates ?? [])
    .filter((t) => (t.templateKind ?? "template") !== "quicksend")
    .map((t) => ({
      name: t.templateName,
      url: t.templateURL,
      description: t.templateDescription,
      type: t.templateDocumentType,
      created: new Date(t.dateCreated),
      updated: new Date(t.dateLastUpdate),
    }))
    .filter((t) => t.name.toLowerCase().includes(q))
    .sort((a, b) => {
      if (sort === "newest") return b.updated.getTime() - a.updated.getTime();
      if (sort === "oldest") return a.updated.getTime() - b.updated.getTime();
      return a.name.localeCompare(b.name);
    });

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="dashboard-loading-card">
          <div className="dashboard-loading-dot" />
          <div className="dashboard-loading-dot" />
          <div className="dashboard-loading-dot" />
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="dashboard">
      <Sidebar active="Templates" />

      <div className="dashboard-main">
        <TrialBanner />

        {/* Header */}
        <header className="dashboard-header">
          <div className="dashboard-header-left">
            <h1 className="dashboard-title">Templates</h1>
            <p className="dashboard-subtitle">
              Start from a premade template or manage your custom documents.
            </p>
          </div>
          <button
            className="primary-button"
            type="button"
            onClick={handleCreateBlank}
          >
            New blank template
          </button>
        </header>

        {/* ========== UNIFIED SEARCH ========== */}
        <div className="tp-search-bar">
          <svg
            className="tp-search-icon"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            className="tp-unified-search"
            type="text"
            placeholder="Search all templates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              className="tp-search-clear"
              type="button"
              onClick={() => setSearch("")}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>

        {/* ========== CATEGORY FILTERS ========== */}
        <div className="tp-category-filters">
          <button
            className={`tp-category-pill${
              !activeCategory ? " tp-category-pill--active" : ""
            }`}
            style={
              !activeCategory
                ? { background: "#0f172a", color: "#fff", borderColor: "transparent" }
                : undefined
            }
            onClick={() => setActiveCategory(null)}
          >
            All
          </button>
          {CATEGORIES.map((cat) => {
            const isActive = activeCategory === cat;
            const colors = CATEGORY_COLORS[cat] ?? {
              bg: "#f3f4f6",
              color: "#374151",
            };
            return (
              <button
                key={cat}
                className={`tp-category-pill${
                  isActive ? " tp-category-pill--active" : ""
                }`}
                style={
                  isActive
                    ? {
                        background: colors.color,
                        color: "#fff",
                        borderColor: "transparent",
                      }
                    : undefined
                }
                onClick={() =>
                  setActiveCategory((prev) => (prev === cat ? null : cat))
                }
              >
                {cat}
              </button>
            );
          })}
        </div>

        {/* ========== FEATURED / POPULAR ========== */}
        {showFeatured && (
          <section className="tp-section">
            <h3 className="tp-section-title">Popular templates</h3>
            <div className="tp-featured-row">
              {filteredFeatured.map((tmpl) => {
                const cat = CATEGORY_COLORS[tmpl.category] ?? {
                  bg: "#f3f4f6",
                  color: "#374151",
                };
                return (
                  <div
                    key={tmpl.key}
                    className="tp-premade-card tp-featured-card"
                    onClick={() =>
                      navigate(
                        `/send?template=${encodeURIComponent(tmpl.key)}`
                      )
                    }
                  >
                    <span
                      className="tp-premade-badge"
                      style={{ background: cat.bg, color: cat.color }}
                    >
                      {tmpl.category}
                    </span>
                    <h4 className="tp-premade-name">{highlightMatch(tmpl.name, search)}</h4>
                    <p className="tp-premade-desc">{tmpl.description}</p>
                    <span className="tp-premade-meta">
                      {tmpl.pageCount} page{tmpl.pageCount > 1 ? "s" : ""}
                    </span>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ========== ALL PREMADE TEMPLATES ========== */}
        <section className="tp-section">
          <div className="tp-all-header">
            <div>
              <h3 className="tp-section-title" style={{ marginBottom: 0 }}>
                {activeCategory ?? "All templates"}
              </h3>
              <span className="tp-section-count">
                {filteredPremade.length} template
                {filteredPremade.length === 1 ? "" : "s"}
                {q && ` matching "${search}"`}
              </span>
            </div>
          </div>

          {filteredPremade.length > 0 ? (
            <div className="tp-premade-grid">
              {filteredPremade.map((tmpl) => {
                const cat = CATEGORY_COLORS[tmpl.category] ?? {
                  bg: "#f3f4f6",
                  color: "#374151",
                };
                return (
                  <div
                    key={tmpl.key}
                    className="tp-premade-card"
                    onClick={() =>
                      navigate(
                        `/send?template=${encodeURIComponent(tmpl.key)}`
                      )
                    }
                  >
                    <span
                      className="tp-premade-badge"
                      style={{ background: cat.bg, color: cat.color }}
                    >
                      {tmpl.category}
                    </span>
                    <h4 className="tp-premade-name">{highlightMatch(tmpl.name, search)}</h4>
                    <p className="tp-premade-desc">{tmpl.description}</p>
                    <span className="tp-premade-meta">
                      {tmpl.pageCount} page{tmpl.pageCount > 1 ? "s" : ""}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="tp-empty">
              <h4 className="tp-empty-title">No templates found</h4>
              <p className="tp-empty-sub">
                Try a different search or category.
              </p>
            </div>
          )}
        </section>

        {/* ========== USER TEMPLATES ========== */}
        <section className="tp-section">
          <div className="tp-user-header">
            <div>
              <h3 className="tp-section-title" style={{ marginBottom: 0 }}>
                Your templates
              </h3>
              <span className="tp-section-count">
                {templatesLoading
                  ? "..."
                  : `${userTemplates.length} template${
                      userTemplates.length === 1 ? "" : "s"
                    }`}
              </span>
            </div>

            <div className="tp-controls">
              <select
                className="tp-sort"
                value={sort}
                onChange={(e) =>
                  setSort(e.target.value as "newest" | "oldest" | "name")
                }
              >
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="name">Name (A-Z)</option>
              </select>
            </div>
          </div>

          {/* Loading */}
          {templatesLoading && (
            <div className="tp-loading">
              <div className="tp-spinner" />
              Loading templates...
            </div>
          )}

          {/* Empty state */}
          {!templatesLoading && userTemplates.length === 0 && (
            <div className="tp-empty">
              <div className="tp-empty-icon">
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#cbd5e1"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="12" y1="18" x2="12" y2="12" />
                  <line x1="9" y1="15" x2="15" y2="15" />
                </svg>
              </div>
              <h4 className="tp-empty-title">No custom templates yet</h4>
              <p className="tp-empty-sub">
                {search
                  ? "No templates match your search."
                  : "Create a blank template or use a premade one above to get started."}
              </p>
              {!search && (
                <button
                  className="primary-button"
                  type="button"
                  onClick={handleCreateBlank}
                  style={{ marginTop: 10 }}
                >
                  Create a template
                </button>
              )}
            </div>
          )}

          {/* Template list */}
          {!templatesLoading && userTemplates.length > 0 && (
            <div className="tp-list">
              {userTemplates.map((t, idx) => (
                <div
                  key={t.url}
                  className="tp-item"
                  onClick={() => navigate(`/template/${t.url}`)}
                >
                  {/* Icon */}
                  <div className="tp-item-icon">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                      <polyline points="10 9 9 9 8 9" />
                    </svg>
                  </div>

                  {/* Info */}
                  <div className="tp-item-info">
                    <span className="tp-item-name">{highlightMatch(t.name, search)}</span>
                    <span className="tp-item-date">
                      Edited{" "}
                      {t.updated.toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </span>
                  </div>

                  {/* Actions */}
                  <div
                    className="tp-item-actions"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      className="tp-action-btn"
                      onClick={() =>
                        navigate(
                          `/send?template=${encodeURIComponent(t.url)}`
                        )
                      }
                    >
                      Use
                    </button>
                    <button
                      className="tp-action-btn"
                      onClick={() => navigate(`/template/${t.url}`)}
                    >
                      Edit
                    </button>

                    {/* Three-dot menu */}
                    <div style={{ position: "relative" }}>
                      <button
                        className="tp-menu-trigger"
                        onClick={() =>
                          setMenuOpen((prev) => (prev === idx ? null : idx))
                        }
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <circle cx="12" cy="5" r="2" />
                          <circle cx="12" cy="12" r="2" />
                          <circle cx="12" cy="19" r="2" />
                        </svg>
                      </button>

                      {menuOpen === idx && (
                        <div className="tp-dropdown">
                          <button
                            className="tp-dropdown-item"
                            onClick={() => {
                              setMenuOpen(null);
                              setRenameTemplate({
                                title: t.name,
                                url: t.url,
                              });
                            }}
                          >
                            Rename
                          </button>
                          <button
                            className="tp-dropdown-item tp-dropdown-danger"
                            onClick={() => {
                              setMenuOpen(null);
                              setRemoveTemplate({
                                name: t.name,
                                url: t.url,
                              });
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Modals */}
      {removeTemplate && (
        <Remove
          templateName={removeTemplate.name}
          templateURL={removeTemplate.url}
          onClose={() => setRemoveTemplate(null)}
          onDeleted={() =>
            fetchUserTemplates().then((t) => {
                            setUserTemplates(t ?? null);
            })
          }
        />
      )}

      {renameTemplate && (
        <Rename
          templateURL={renameTemplate.url}
          currentTitle={renameTemplate.title}
          onClose={() => setRenameTemplate(null)}
          onRenamed={() =>
            fetchUserTemplates().then((t) => {
                            setUserTemplates(t ?? null);
            })
          }
        />
      )}
    </div>
  );
}

export default TemplatesPage;
